import React from 'react';
import { DateRange } from '@blueprintjs/datetime';
import { FormState } from 'formstate';
import { IItemsList } from 'welltrax-ui-components';
import {
  cloneDeep,
  filter,
  find,
  findIndex,
  get,
  isArray,
  lowerCase,
  map,
  startsWith,
  template,
  uniqBy,
  upperCase
} from 'lodash';
import moment from 'moment';

import { AppContextInterface } from '../../../../../App';
import { DynamicFieldType } from '../../../../../modules/commons/components/dynamicField/types/types';
import { ActionType } from '../../../../types/ActionType';
import { SelectUnselectSaveType } from '../../../../types/ItemSelectType';
import {
  IColumnsRep,
  IConditionalsType,
  IFilterInputType,
  IFilterType,
  IProfileType,
  IVeltraxProfileType,
  ProfileLevelType,
  ProfileModes
} from '../../../../types/ProfileTypes';
import { intl } from '../../../../utils/Intl';
import { ADVANCE_HIDE_SECTION_TYPE } from './Constant';
import { ColStyled, FieldContent, FilterLabel } from './styles';
import authService from '../../../../services/AuthServices';
import appStore from '../../../../stores/AppStore';
import FilterField from '../FilterProfileTab/Filters/FilterField';

const setListFieldValue = (items: IFilterType[] | IConditionalsType[], field: IFilterType | IConditionalsType) => {
  const cloneItems = cloneDeep(items);
  if (cloneItems.length > 0) {
    const index = findIndex(cloneItems, { field: field.field });
    cloneItems[index] = field;
  }
  return cloneItems;
};

const compareProfileDataConfiguration = (
  items: IConditionalsType[] | IFilterType[],
  compare: IConditionalsType[] | IFilterType[]
): boolean => {
  let hasDifference = false;
  if (items.length !== compare.length) {
    return true;
  }
  for (let i = 0; i < items.length; i++) {
    const item: IConditionalsType | IFilterType = items[i];
    const compareItem: IConditionalsType | IFilterType = compare[i];
    if (
      JSON.stringify(item.value) !== JSON.stringify(compareItem.value) ||
      item.operator !== compareItem.operator ||
      compareIfExist('format', item, compareItem) ||
      compareIfExist('dateType', item, compareItem) ||
      compareIfExist('isSpecific', item, compareItem)
    ) {
      hasDifference = true;
      break;
    }
  }

  return hasDifference;
};

const compareIfExist = (key: string, item: any, compareItem: any) =>
  item.hasOwnProperty(key) && compareItem.hasOwnProperty(key) && get(item, key) !== get(compareItem, key);

export const renderFilterInForm = (
  filter: IFilterType,
  key: string,
  onChange: (value: any) => void,
  advance?: boolean,
  inDrawer?: boolean,
  padding?: string,
  margin?: string,
  getValueOnSelectMenuItem?: boolean
) => {
  const titleOptions = filter?.label?.trimRight()?.trimLeft();
  const notFilter = (type?: IFilterInputType) => filter.type !== type;
  const isTwoColumn =
    advance &&
    notFilter('ADVANCE_SECTION') &&
    notFilter('ADVANCE_HIDE_SECTION') &&
    notFilter('BOOLEAN') &&
    notFilter('DATERANGE') &&
    notFilter('CUSTOM_DATE_RANGE') &&
    notFilter('GROUP_BUTTONS');
  const notLabel = notFilter('ADVANCE_SECTION') && notFilter('ADVANCE_HIDE_SECTION');
  const label = !!titleOptions ? titleOptions : '';

  return (
    <ColStyled
      xs={12}
      sm={12}
      md={12}
      lg={isTwoColumn ? 6 : 12}
      xl={isTwoColumn ? 6 : 12}
      key={key}
      type={filter?.type}
      padding={padding}
      margin={margin}
    >
      <FieldContent type={filter?.type}>
        {notLabel && <FilterLabel title={label}>{label}:</FilterLabel>}
        <div className={'gsi-filter-field'} style={{ width: '100%' }}>
          <FilterField
            key={filter.field}
            showLabel={false}
            minimal={false}
            filter={filter}
            isQueryFilterInForm={true}
            onChange={(value: any) => {
              if (onChange) {
                onChange(value);
              }
            }}
            advance={advance}
            getValueOnSelectMenuItem={getValueOnSelectMenuItem}
          />
        </div>
      </FieldContent>
    </ColStyled>
  );
};

const includeTimeToDate = (date: Date | null, hour: number, minute: number) => {
  return date
    ? moment(date)
        .set({
          hour,
          minute
        })
        .toDate()
    : null;
};

// imports must include all the modules necessary to calculate the value of each filter.
const evalStringToCode = (str: string) => {
  return template(`<% print(${str}) %>`, { imports: { moment } })();
};

const getConditionalLabel = (
  filter: IFilterType,
  appContext: AppContextInterface,
  isLabel?: boolean
): string | null => {
  if (
    filter?.conditionalInContext &&
    filter?.labelConditional &&
    appContext.app.filterContext?.hasOwnProperty(filter.conditionalInContext)
  ) {
    const contextValue = appContext.app.filterContext[filter.conditionalInContext];
    const isNullConditional = contextValue === '' && filter.labelConditional.hasOwnProperty('null');
    if (contextValue || isNullConditional) {
      const key = isNullConditional ? 'null' : contextValue;
      return isLabel ? filter.labelConditional[key] : null;
    }
  }
  return null;
};

const calculateFilterValue = (filter: IFilterType): IFilterType => {
  const filterNew = cloneDeep(filter);

  const validateIfArray = () => {
    if (isArray(filterNew?.value)) {
      return filterNew.value.some((e: any) => !e);
    }
    return true;
  };
  if (filterNew?.calculateValue && filterNew.value && filterNew.value !== '' && validateIfArray()) {
    filterNew.value = isArray(filterNew.calculateValue)
      ? filterNew.calculateValue.map((item: any) => evalStringToCode(item))
      : evalStringToCode(filterNew.calculateValue);

    return filterNew;
  }
  if (
    filterNew?.calculateValue &&
    ['DATE', 'DATETIME'].includes(filterNew.type || '') &&
    moment(evalStringToCode(filterNew.calculateValue)).isValid()
  ) {
    filterNew.value = evalStringToCode(filterNew.calculateValue);
    return filterNew;
  }
  if (
    filterNew?.calculateValue &&
    ['DATE', 'DATETIME'].includes(filterNew.type || '') &&
    moment(evalStringToCode(filterNew.calculateValue)).isValid()
  ) {
    filterNew.value = evalStringToCode(filterNew.calculateValue);
    return filterNew;
  }

  return filterNew;
};

const getInactiveStatus = (status: boolean) => (!status ? intl('app.statusInactive', '(INACTIVE)') : '');

const containsValue = (elementSearch: any, value: string) => {
  if (value !== '') {
    return lowerCase(elementSearch).includes(lowerCase(value));
  }
  return true;
};

const compareRange = (dateRange1: DateRange, dateRange2: DateRange): boolean => {
  const [dateStart1, dateEnd1] = dateRange1;
  const [dateStart2, dateEnd2] = dateRange2;

  if (dateStart1 && dateStart2 && dateEnd1 && dateEnd2) {
    return moment(dateStart1).isSame(dateStart2, 'minute') && moment(dateEnd1).isSame(dateEnd2, 'minute');
  }
  return false;
};

const convertDateRange = (dateRange: DateRange): DateRange => {
  const [date1, date2] = dateRange;
  return [date1 ? moment(date1).toDate() : null, date2 ? moment(date2).toDate() : null];
};

const isDateRange = (value: any) => isArray(value) && value.length === 2 && value[0] && value[1];

const filterActiveValue = <T extends IFilterType | IConditionalsType>(value: T[]): T[] =>
  value.filter((item: T) => item?.active);

const generateQueryFilters = (form: FormState<any>) => {
  const items = form.$.queryFilters.value;
  const includeHideSection = find(items, { type: ADVANCE_HIDE_SECTION_TYPE });
  const queryFilters: any[] = !!includeHideSection
    ? items.filter((value: IFilterType) => !value.hidden)
    : items.filter((value: IFilterType) => value.active && !value.hidden);
  queryFilters.forEach((filter: IFilterType, index: number) => {
    if (!!filter?.filters?.length) {
      queryFilters.splice(index + 1, 0, ...filter.filters);
    }
  });
  return queryFilters ?? [];
};

const isQueryFiltersUpdate = (
  defaultQueryFilters: IFilterType[],
  formQueryFilters: IFilterType[],
  previousQueryFilters: IFilterType[]
): boolean => {
  return (
    !compareProfileDataConfiguration(defaultQueryFilters, formQueryFilters) &&
    compareProfileDataConfiguration(defaultQueryFilters, previousQueryFilters)
  );
};

const reorderRows = (items: IColumnsRep[], indexNew: number, indexOld: number) => {
  const result = cloneDeep(items);
  let init: number = indexNew + 1;
  let end: number = indexOld;
  let normalFlow = true;

  if (indexNew > indexOld) {
    init = indexOld;
    end = indexNew - 1;
    normalFlow = false;
  }

  for (let i = init; i <= end; i++) {
    const pos = normalFlow ? i - 1 : i + 1;
    const aux: IColumnsRep = items[pos];
    aux.order = i + 1;
    result[i] = aux;
  }

  const aux: IColumnsRep = items[indexOld];
  aux.order = indexNew + 1;
  result[indexNew] = aux;

  return result;
};

const isCustom = (type?: DynamicFieldType) =>
  type && type !== 'CUSTOM_TOP_GAUGE_GROSS_BARREL' && type !== 'CUSTOM_BOTTOM_GAUGE_GROSS_BARREL'
    ? startsWith(type.toLowerCase(), 'custom')
    : false;

const getDefaultProfileName = (profile: IProfileType) => {
  switch (profile.type) {
    case 'OPERATOR':
      return 'OPERATOR DEFAULT';
    case 'TERMINAL':
      return 'TERMINAL DEFAULT';
    case 'DRIVER':
      return 'DRIVER DEFAULT';
    case 'DRIVERSCHEDULE':
      return 'Driver Schedule DEFAULT';
    case 'SCHEDULE_MASTER':
      return 'Schedule Master Profile';
    case 'TOTALS':
      return 'TOTALS DEFAULT';
    case 'PICKUP':
      return 'PICKUP DEFAULT';
    case 'DROPOFF':
      return 'DROPOFF DEFAULT';
    case 'TRUCKTRAILER':
      return 'TRUCKTRAILER DEFAULT';
    case 'CONTRACTOR':
      return 'CONTRACTOR DEFAULT';
    case 'ACCOUNT':
      return 'ACCOUNT DEFAULT';
    case 'MANAGE_LOAD':
      return 'MANAGE LOAD DEFAULT';
    case 'MANAGE_SCENARIO':
      return 'MANAGE SCENARIO DEFAULT';
    case 'SECONDARY_SCENARIO':
      return 'SECONDARY SCENARIO DEFAULT';
    case 'DISPATCH_DRIVER':
      return 'DISPATCH DRIVER DEFAULT';
    case 'DISPATCH_LOAD':
      return 'DISPATCH LOAD DEFAULT';
    case 'DISPATCH_DRIVER_DETAILS':
      return 'DISPATCH DRIVER DETAILS DEFAULT';
    case 'DISPATCH_DRIVER_WORKING_TODAY':
      return 'DISPATCH DRIVER WORKING TODAY DEFAULT';
    case 'REVIEW_RECONCILE_LOAD':
      return 'REVIEW RECONCILE LOAD DEFAULT';
    case 'REVIEW_DVIR':
      return 'REVIEW DVIR DEFAULT';
    case 'REVIEW_DRIVER_ADDED_TANKS':
      return 'REVIEW DRIVER ADDED TANKS DEFAULT';
    case 'FUEL_PURCHASE':
      return 'FUEL PURCHASE DEFAULT';
    case 'VEHICLE_MAINTENANCE':
      return 'VEHICLE MAINTENANCE DEFAULT';
    case 'REVIEW_RTU':
      return 'REVIEW RTU DEFAULT';
    case 'REVIEW_STATION_LOGS_TICKETS':
      return 'REVIEW STATION LOGS TICKETS DEFAULT';
    case 'REVIEW_STATION_LOGS_OPEN_CLOSE':
      return 'REVIEW STATION LOGS OPEN CLOSE DEFAULT';
    case 'MASTER_INVOICE':
      return 'MASTER INVOICE PROFILE';
    case 'INVOICE_TOTALS':
      return 'INVOICE TOTALS DEFAULT';
    case 'DISPATCH_DRIVER_SHIFT_EMAIL':
      return 'DEFAULT';
    case 'INVOICE_TICKETS_DETAILS':
      return 'INVOICE TICKETS DEFAULT';
    case 'FINANCE_RATE_SHEET':
      return 'FINANCE RATESHEET DEFAULT';
    case 'FINANCE_CONTRACT':
      return 'FINANCE CONTRACT DEFAULT';
    case 'MASTER_PAYROLL':
      return 'MASTER PAYROLL PROFILE';
    case 'PAYROLL_TICKETS_DETAILS':
      return 'PAYROLL TICKETS DEFAULT';
    case 'PAYROLL_SETTLEMENTS':
      return 'PAYROLL SETTLEMENTS DEFAULT';
    case 'STATION_LOG_DRIVER':
      return 'STATION LOG DRIVER DEFAULT';
    case 'SETUP_CONTRACT':
      return 'FINANCE CONTRACT DEFAULT';
  }
};

const getDefaultProfilePerModule = (profiles: IProfileType[]) => {
  return profiles.find(profile => profile.name === getDefaultProfileName(profile));
};

const populateFiltersWithCommodity = (filters: IFilterType[]) => {
  const commodityId = appStore.selectedCommodity;
  if (commodityId) {
    const commodityIndex = findIndex(filters, {
      field: 'commodityId'
    });
    if (commodityIndex !== -1 && filters[commodityIndex].active && !filters[commodityIndex].value) {
      filters[commodityIndex] = {
        ...filters[commodityIndex],
        value: [commodityId]
      };
    }
  }
  return filters;
};

const processFiltersBeforeForm = (filters: IFilterType[]) => {
  return map(filters, (value: IFilterType) => {
    return calculateFilterValue(value);
  });
};

const mapConditionalsToReadyFormat = (conditionals: IConditionalsType[]) => {
  return conditionals.map((value: IConditionalsType) => {
    if (!value.hasOwnProperty('format')) {
      return { ...value, format: '' };
    }
    return value;
  });
};

const getProfileForInvoicePayroll = (
  profile: IVeltraxProfileType,
  dynamicFieldsAsColumns: IColumnsRep[] = []
): IProfileType => {
  try {
    const data = JSON.parse(profile.preference);
    const col = data.columns ?? [];
    const newTotalsColumns = cloneDeep(dynamicFieldsAsColumns);
    newTotalsColumns.forEach(item => {
      const foundedCol = col.find((el: any) => el.name === item.name);
      if (foundedCol) {
        item.visible = foundedCol.visible;
        item.order = foundedCol.order;
      }
    });
    return {
      layout: [],
      pivot: [],
      id: profile.id,
      user_id: profile.createdByUser ? profile.createdByUser.id || '0' : '0',
      created_by: profile.createdByUser ? profile.createdByUser.name : '',
      name: profile.name,
      type: profile.type,
      is_global: profile.isGlobal,
      profileLevel: profile?.profileLevel ?? 'USER',
      userGroupsIds: profile?.userGroupsIds ?? [],
      editUsersIds: profile?.editUsersIds ?? [],
      viewUsersIds: profile?.viewUsersIds ?? [],
      isActive: profile.isActive,
      is_default: profile.isDefault,
      is_read_only: profile.isReadOnly,
      columns: newTotalsColumns,
      filters: populateFiltersWithCommodity([]),
      conditionals: [],
      details_locked: false,
      details_collapse: false
    };
  } catch (e) {
    throw e;
  }
};

const getProfile = (profile: IVeltraxProfileType) => {
  const data = JSON.parse(profile.preference);
  const col = data && data.columns ? data.columns : [];
  const fil = data && data.filters ? data.filters : [];
  const cond = data && data.conditionals ? data.conditionals : [];
  const pivot = data && data.pivot ? data.pivot : [];
  const locked = data && data.details_locked ? data.details_locked : false;
  const collapse = data && data.details_collapse ? data.details_collapse : false;
  const layout = data && data.layout ? data.layout : [];
  const queryFilters = data && data.queryFilters ? data.queryFilters : [];
  const mode = data && data.mode ? data.mode : ProfileModes.BASIC;

  return {
    layout,
    queryFilters,
    pivot,
    mode,
    id: profile.id,
    user_id: profile?.createdByUser?.id ?? '0',
    created_by: profile?.createdByUser?.name ?? '',
    name: profile.name,
    type: profile.type,
    profileLevel: profile?.profileLevel ?? 'USER',
    userGroupsIds: profile?.userGroupsIds ?? [],
    editUsersIds: profile?.editUsersIds ?? [],
    viewUsersIds: profile?.viewUsersIds ?? [],
    isActive: profile.isActive,
    is_global: profile.isGlobal,
    is_default: profile.isDefault,
    is_read_only: profile.isReadOnly,
    columns: uniqBy(col, (col: IColumnsRep) => [col.name, col.commodity].join()),
    filters: processFiltersBeforeForm(fil),
    conditionals: mapConditionalsToReadyFormat(cond),
    details_locked: locked,
    details_collapse: collapse
  };
};

const assingUnAssignUser = (
  keyForm: 'editUsersIds' | 'viewUsersIds',
  userBasic: any[],
  formProfile: FormState<any>
) => {
  const assigned: IItemsList[] = [];
  const unassigned: IItemsList[] = [];

  userBasic
    ?.filter(item => item.id !== formProfile.$.user_id.value)
    ?.forEach(item => {
      const text = `${upperCase(item.description)}  (${item.name})`;

      if (formProfile.$[keyForm].value.includes(item.id)) {
        assigned.push({
          text,
          value: item.id
        });
      } else {
        unassigned.push({
          text,
          value: item.id
        });
      }
    });

  return { assigned, unassigned };
};

const onAssingUserProfile = (userBasic: any[], form: FormState<any>) => {
  const { assigned: assignedEditer } = ProfileUtils.assingUnAssignUser('editUsersIds', userBasic, form);
  const { assigned: assignedViewer } = ProfileUtils.assingUnAssignUser('viewUsersIds', userBasic, form);
  return { assignedEditer: assignedEditer.map(it => it.value), assignedViewer: assignedViewer.map(it => it.value) };
};

const onSelectViewUser = (userList: number[], form: FormState<any>) => {
  const viewUser = filter(cloneDeep(form.$.editUsersIds.value), item => userList.includes(item));
  form.$.editUsersIds.onChange(viewUser);
};

const onSelectEditUser = (userList: number[], form: FormState<any>) => {
  const viewUser = form.$.viewUsersIds.value;
  userList.forEach(item => !viewUser.includes(item) && viewUser.push(item));
  form.$.viewUsersIds.onChange(viewUser);
};

const onSaveSelectionUsers = (
  keyForm: 'editUsersIds' | 'viewUsersIds',
  form: FormState<any>,
  { itemsAssigned }: SelectUnselectSaveType
) => {
  const userList = itemsAssigned.map(({ value }) => parseInt(value, 10));
  form.$[keyForm].onChange(userList);
  keyForm === 'editUsersIds' ? onSelectEditUser(userList, form) : onSelectViewUser(userList, form);
};

const initUserWhenCreateProfile = (status: ActionType, userBasic: any[], form: FormState<any>) => {
  if (status === 'CREATE') {
    const users = userBasic.filter(item => item.id !== form.$.user_id.value).map(item => item.id);
    form.$.editUsersIds.onChange(users);
    form.$.viewUsersIds.onChange(users);
  } else if (status === 'EDIT') {
    const { assignedEditer, assignedViewer } = onAssingUserProfile(userBasic, form);
    form.$.editUsersIds.onChange(assignedEditer);
    form.$.viewUsersIds.onChange(assignedViewer);
  }
};

const onChangeUserGroup = async (
  values: any[],
  status: ActionType,
  form: FormState<any>,
  basicListByTypeStore: any
) => {
  if (!!values?.length) {
    const groupName = basicListByTypeStore?.userGroups
      ?.filter((item: any) => values.includes(item.id))
      ?.map((item: any) => item.description);

    if (!!groupName?.length) {
      await basicListByTypeStore.getUserBasic({
        groupName,
        orgID: authService.getOrgId()
      });

      initUserWhenCreateProfile(status, basicListByTypeStore.userBasic, form);
    }
  } else {
    basicListByTypeStore.setUserBasic([]);
    form.$.editUsersIds.onChange([]);
    form.$.viewUsersIds.onChange([]);
  }
};

const onChangeProfileLevel = async (
  value: ProfileLevelType,
  status: ActionType,
  form: FormState<any>,
  basicListByTypeStore: any,
  opening?: boolean
) => {
  form.$.userGroupsIds.onChange([]);
  form.$.editUsersIds.onChange([]);
  form.$.viewUsersIds.onChange([]);

  if (value === 'COMPANY') {
    await basicListByTypeStore.getUserBasic({
      orgID: authService.getOrgId()
    });

    initUserWhenCreateProfile(status, basicListByTypeStore.userBasic, form);
  } else if (value === 'USER_GROUP') {
    await basicListByTypeStore.getUserGroups();

    if (!opening) {
      const userGroupsList = basicListByTypeStore?.userGroups.map((item: any) => item.id);
      form.$.userGroupsIds.onChange(userGroupsList);
      await onChangeUserGroup(userGroupsList, status, form, basicListByTypeStore);
    }
  }
};

const onDuplicateProfile = (item: any, setProfile: (profile: any) => void, setStatus: (status: ActionType) => void) => {
  const profile: any = cloneDeep(item || {});
  profile.name = '';
  profile.created_by = authService.getUser().username;
  profile.user_id = authService.getUser().id;
  delete profile.id;
  profile.is_read_only = false;

  setProfile(profile);
  setStatus('CREATE');
};

const autoSelectDefaultProfile = (
  form: FormState<any>,
  profiles: any[],
  default_profile: any,
  profile: any,
  setProfile: (profile: any) => void,
  parseProfile: (profile: any) => void
): Promise<boolean> => {
  if (!form.$.isActive.value) {
    const active: any = find(profiles, { isDefault: true });

    setProfile(
      (active ? parseProfile(cloneDeep(active)) : default_profile ? default_profile : profile) as IProfileType
    );

    return Promise.resolve(active || default_profile);
  }
  return Promise.resolve(false);
};

const getActiveProfile = async (
  form: FormState<any>,
  profileIn: any,
  defaultProfile: any,
  profiles: any[],
  setProfile: (profile: any) => void,
  parseProfile: (profile: any) => any
) => {
  if (profileIn) {
    const profile: any = find(profiles, { id: profileIn.id });
    if (profile) {
      const prof = await parseProfile(profile);
      setProfile({ ...prof, queryFilters: form.$.queryFilters.value });
      return;
    }
  }
  const active: any = find(profiles, { isDefault: true });
  if (active) {
    const prof = await parseProfile(active);
    setProfile(prof);
    return;
  }

  if (defaultProfile) {
    setProfile(defaultProfile);
    return;
  }
};

const activateProfile = async (
  profile: any,
  form: FormState<any>,
  status: ActionType,
  updateProfile: () => any
): Promise<boolean> => {
  try {
    if (!!profile) {
      profile.is_default = true;
      form.$.is_default.onChange(true);
      if (profile?.queryFilters) {
        form.$.queryFilters.onChange(profile.queryFilters);
      }

      if (status !== 'CREATE') {
        const response = await updateProfile();
        if (response) {
          return Promise.resolve(true);
        }
      }
    }
    return Promise.resolve(false);
  } catch (error) {
    throw (error as Error).message;
  }
};

const removeDuplicateField = (elements: (IFilterType | IConditionalsType)[]): (IFilterType | IConditionalsType)[] =>
  uniqBy([...elements], 'field');

const fieldsWithOptions = [
  'SELECT',
  'MULTI_SELECT',
  'GROUP_BUTTONS',
  'MULTI_SELECT_TAGS',
  'AUTOCOMPLETE',
  'CUSTOM_TIME_SELECT'
];

export const ProfileUtils = {
  fieldsWithOptions,
  setListFieldValue,
  compareProfileDataConfiguration,
  renderFilterInForm,
  includeTimeToDate,
  evalStringToCode,
  getConditionalLabel,
  calculateFilterValue,
  getInactiveStatus,
  containsValue,
  compareRange,
  convertDateRange,
  isDateRange,
  filterActiveValue,
  generateQueryFilters,
  isQueryFiltersUpdate,
  reorderRows,
  isCustom,
  getDefaultProfilePerModule,
  getProfile,
  mapConditionalsToReadyFormat,
  processFiltersBeforeForm,
  getProfileForInvoicePayroll,
  assingUnAssignUser,
  onAssingUserProfile,
  onSaveSelectionUsers,
  onSelectViewUser,
  onSelectEditUser,
  initUserWhenCreateProfile,
  onChangeUserGroup,
  onChangeProfileLevel,
  onDuplicateProfile,
  autoSelectDefaultProfile,
  getActiveProfile,
  activateProfile,
  removeDuplicateField
};
